import React from 'react';
import './CategoryEmblem.css';

type EmblemDef = { bg: string; label: string; glyph: React.ReactNode };

const glyph = (d: string) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>
    <path d={d} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Эмблемы основных категорий FreelanCYX (ключ профиля / orderKey). */
const EMBLEMS: Record<string, EmblemDef> = {
  design: {
    bg: '#3b6cff',
    label: 'Дизайн',
    glyph: glyph('M4 20l4.5-1.5L19 8l-3-3L5.5 15.5 4 20Zm11.5-14.5 3 3')
  },
  web: {
    bg: '#1aa6a0',
    label: 'Веб',
    glyph: (
      <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>
        <path d="M8 8 4 12l4 4M16 8l4 4-4 4M13 6l-2 12" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  bots: {
    bg: '#7b5cff',
    label: 'Боты',
    glyph: glyph('M8 9h8v8a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V9Zm2-4h4v4H10V5Zm-3 8H5m14 0h-2')
  },
  mobile: {
    bg: '#2f7bff',
    label: 'Мобайл',
    glyph: glyph('M9 3h6a1.5 1.5 0 0 1 1.5 1.5v15A1.5 1.5 0 0 1 15 21H9a1.5 1.5 0 0 1-1.5-1.5v-15A1.5 1.5 0 0 1 9 3Zm3 15.2h.01')
  },
  scripts_ai: {
    bg: '#e07a2f',
    label: 'AI',
    glyph: glyph('M7 4h10v16H7V4Zm3 3h4M8 12h8M8 16h5')
  },
  texts: {
    bg: '#1eb8c7',
    label: 'Тексты',
    glyph: glyph('M6 5h12v14H6V5Zm3 4h6M9 12h6M9 15h4')
  },
  text: {
    bg: '#1eb8c7',
    label: 'Тексты',
    glyph: glyph('M6 5h12v14H6V5Zm3 4h6M9 12h6M9 15h4')
  },
  translation: {
    bg: '#e2557a',
    label: 'Переводы',
    glyph: (
      <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>
        <path d="M4 6h9M8.5 6v2a6 6 0 0 0 6 6h.5M12 20l3-8 3 8M13.2 17h3.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  marketing: {
    bg: '#9b4dff',
    label: 'Маркетинг',
    glyph: glyph('M4 14v-4l8-4v12l-8-4Zm10-6 5-2v12l-5-2')
  },
  video_audio: {
    bg: '#3a7bff',
    label: 'Видео',
    glyph: glyph('M5 7h9a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Zm11 3 5-2.5v9L16 14')
  },
  business: {
    bg: '#1aa88a',
    label: 'Бизнес',
    glyph: glyph('M4 19h16M6 16V10m4 6V7m4 9v-5m4 5V9')
  },
  '3d_animation': {
    bg: '#d94f9a',
    label: '3D',
    glyph: glyph('M12 3 20 7.5v9L12 21l-8-4.5v-9L12 3Zm0 18V12m0 0 8-4.5M12 12 4 7.5')
  },
  music: {
    bg: '#8b5cf6',
    label: 'Музыка',
    glyph: glyph('M9 18a2.5 2.5 0 1 1-2.5-2.5H9V6l10-2v10.5a2.5 2.5 0 1 1-2.5-2.5H19')
  },
  other: {
    bg: '#6b7785',
    label: 'Прочее',
    glyph: (
      <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden>
        <circle cx="6" cy="12" r="1.7" />
        <circle cx="12" cy="12" r="1.7" />
        <circle cx="18" cy="12" r="1.7" />
      </svg>
    )
  },
  custom: {
    bg: '#6b7785',
    label: 'Прочее',
    glyph: (
      <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden>
        <circle cx="6" cy="12" r="1.7" />
        <circle cx="12" cy="12" r="1.7" />
        <circle cx="18" cy="12" r="1.7" />
      </svg>
    )
  }
};

function resolveKey(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return 'other';
  if (EMBLEMS[s]) return s;
  // sub:mainKey:subKey or customMain:
  if (s.startsWith('sub:')) {
    const main = s.slice(4).split(':')[0];
    if (main && EMBLEMS[main]) return main;
  }
  if (s.startsWith('customMain:') || s.startsWith('customSub:')) return 'other';
  const base = s.split(/[:/|>]/)[0]?.trim();
  if (base && EMBLEMS[base]) return base;
  return 'other';
}

export function getCategoryEmblem(raw: unknown): EmblemDef {
  return EMBLEMS[resolveKey(raw)] || EMBLEMS.other;
}

type Props = {
  category: unknown;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  title?: string;
};

export function CategoryEmblem({ category, size = 'md', className = '', title }: Props) {
  const def = getCategoryEmblem(category);
  return (
    <span
      className={`fc-cat-emblem fc-cat-emblem--${size}${className ? ` ${className}` : ''}`}
      style={{ background: def.bg }}
      title={title || def.label}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
    >
      {def.glyph}
    </span>
  );
}

/** Чип выбора категории с эмблемой */
export function CategoryEmblemChip({
  value,
  label,
  selected,
  onSelect
}: {
  value: string;
  label: string;
  selected: boolean;
  onSelect: (v: string) => void;
}) {
  return (
    <button
      type="button"
      className={`fc-cat-chip${selected ? ' fc-cat-chip--on' : ''}`}
      onClick={() => onSelect(value)}
      aria-pressed={selected}
    >
      <CategoryEmblem category={value} size="sm" />
      <span className="fc-cat-chip__label">{label}</span>
    </button>
  );
}
